import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  UserCheck,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Printer,
  Mail,
  Phone,
  EyeOff,
  Calendar,
  Video,
  Check,
  X,
  Zap,
  Briefcase,
  ClipboardList,
  Send,
  Save,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Hand,
  Lock,
  Clock,
  FileText,
  SkipForward,
  RefreshCw,
  CheckCircle2,
  StickyNote,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import ScoreRing from 'src/components/common/ScoreRing';
import MarkdownViewer, {
  markdownToHTML,
  htmlToMarkdown,
} from 'src/components/common/MarkdownViewer';
import MarkdownEditor from 'src/components/common/MarkdownEditor';
import FilterButton from 'src/components/common/FilterButton';
import CandidateProfileModal from 'src/components/Modals/CandidateProfileModal';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useToast } from 'src/hooks/use-toast';
import { useAuth } from 'src/hooks/useAuth';
import {
  useInterviewBrief,
  useInterviewQuestionnaire,
  useInterviewNotes,
} from 'src/services/api/queries/interviewerQueries';
import { useOpportunityDetails } from 'src/services/api/queries/opportunityQueries';
import {
  useSubmitQuestionResponse,
  useSubmitVerdict,
  useSaveInterviewNotes,
} from 'src/services/api/mutations/interviewerMutations';
import { cn, normalizeCtcToLpa } from 'src/lib/utils';

// Only two groups exist for real (verify/clarify) — the reference design's
// "Role scenarios"/"Client & stakeholder" groups had no backing data and
// were dropped, per explicit instruction, rather than left in with nothing
// to fill them.
const QUESTION_GROUPS = [
  {
    key: 'verify',
    icon: UserCheck,
    title: 'Verify the shortlist',
    sub: 'The claims that made this profile a match. Credit nothing until it survives one honest probe.',
  },
  {
    key: 'clarify',
    icon: AlertTriangle,
    title: 'Clarify gaps & unknowns',
    sub: 'What the scorer discounted or could not see. The requirement needs these closed before a decision.',
  },
];

const TIER_META = {
  best: {
    label: 'Best Match',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    ring: '#0D9668',
  },
  near: {
    label: 'Near Match',
    badgeClass: 'bg-amber-100 text-amber-700',
    ring: '#B45309',
  },
  explore: {
    label: 'Explore',
    badgeClass: 'bg-muted text-muted-foreground',
    ring: '#66707F',
  },
};

// match.match_type has been seen as "EXPLORE" and "NEAR_MATCH" — normalize
// by stripping underscores and a trailing "match" so both "NEAR_MATCH" and
// a bare "NEAR" resolve to the same `near` bucket. Truly unrecognized values
// fall back to a neutral style + a space-joined, title-cased version of
// whatever the backend sent (never a raw "Near_match" with the underscore
// showing), rather than guessing at a color scheme for it.
const getTierMeta = (matchType) => {
  const normalized = (matchType || '').toLowerCase().replace(/_/g, '');
  const key =
    normalized.endsWith('match') && normalized !== 'match'
      ? normalized.slice(0, -5)
      : normalized;
  if (TIER_META[key]) return { key, ...TIER_META[key] };
  const label = matchType
    ? matchType
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : 'Unscored';
  return {
    key: 'unknown',
    label,
    badgeClass: 'bg-muted text-muted-foreground',
    ring: '#66707F',
  };
};

const AVATAR_TONE_CLASS = {
  1: 'bg-indigo-100 text-indigo-700',
  2: 'bg-violet-100 text-violet-700',
  3: 'bg-teal-100 text-teal-700',
  4: 'bg-sky-100 text-sky-700',
  5: 'bg-rose-100 text-rose-700',
  6: 'bg-slate-100 text-slate-700',
};

// No avatar tone comes from the API — pick a stable one per candidate so it
// doesn't change on every refetch/pager move.
const avatarToneFor = (id) => {
  const str = String(id || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash + str.charCodeAt(i)) % 6;
  return hash + 1;
};

const SOURCE_TONE_CLASS = {
  claim: 'border-primary/25 bg-primary/10 text-primary',
  skill: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  gap: 'border-amber-200 bg-amber-50 text-amber-700',
  flag: 'border-amber-200 bg-amber-50 text-amber-700',
  role: 'border-border bg-muted text-muted-foreground',
  cand: 'border-primary/25 bg-primary/10 text-primary',
};

const RATING_META = {
  strong: {
    label: 'Strong',
    activeClass: 'border-emerald-300 bg-emerald-100 text-emerald-700',
    dotClass: 'bg-emerald-500',
  },
  mixed: {
    label: 'Mixed',
    activeClass: 'border-amber-300 bg-amber-100 text-amber-700',
    dotClass: 'bg-amber-500',
  },
  weak: {
    label: 'Weak',
    activeClass: 'border-red-300 bg-red-100 text-red-700',
    dotClass: 'bg-red-500',
  },
};

// This app's rating keys -> the API's own read_inference enum. 'skip' has no
// chip in RATING_META above — it's not a read, it's a separate "I'm not
// asking this one" action (see SkipAction) — but it's still a real
// read_inference value the API expects, so the mapping stays here.
const RATING_TO_API = {
  strong: 'STRONG',
  mixed: 'MIXED',
  weak: 'WEAK',
  skip: 'SKIPPED',
};

// The reverse — used to pre-fill a question's chip from a previously saved
// `responses[]` entry (this interviewer's own prior read_inference).
const API_TO_RATING = Object.fromEntries(
  Object.entries(RATING_TO_API).map(([k, v]) => [v, k])
);

const REC_META = {
  adv: {
    label: 'Advance',
    activeClass: 'border-emerald-300 bg-emerald-100 text-emerald-700',
    dotClass: 'bg-emerald-500',
  },
  hold: {
    label: 'Hold',
    activeClass: 'border-amber-300 bg-amber-100 text-amber-700',
    dotClass: 'bg-amber-500',
  },
  no: {
    label: "Don't advance",
    activeClass: 'border-red-300 bg-red-100 text-red-700',
    dotClass: 'bg-red-500',
  },
};

// This app's recommendation keys -> the API's own final_verdict enum.
const RECOMMENDATION_TO_API = {
  adv: 'ADVANCE',
  hold: 'HOLD',
  no: 'REJECT',
};

// The reverse — used to pre-fill the verdict modal from a previously
// submitted `verdicts[]` entry (this interviewer's own prior final_verdict).
const API_TO_RECOMMENDATION = Object.fromEntries(
  Object.entries(RECOMMENDATION_TO_API).map(([k, v]) => [v, k])
);

const REC_ICONS = { adv: ThumbsUp, hold: Hand, no: ThumbsDown };

const EMPTY_SCORECARD = {
  ratings: {},
  notes: {},
  checklist: {},
  recommendation: null,
  verdictComments: '',
  submittedAt: null,
  contactRevealed: false,
};

const FLAG_LABELS = {
  short_stint: (f) => `${f.months} mo stint`,
};
const formatTimelineFlag = (f) =>
  FLAG_LABELS[f.type] ? FLAG_LABELS[f.type](f) : f.type.replace(/_/g, ' ');

// The API always returns the real email/phone — there's no backend-provided
// masked variant. The reveal-on-demand interaction is still worth keeping
// (nothing shows on screen until asked for), so the mask is built here.
const maskEmail = (email) => {
  if (!email) return '—';
  const at = email.indexOf('@');
  if (at === -1) return '•'.repeat(email.length);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const tld = dot === -1 ? '' : domain.slice(dot);
  return `${'•'.repeat(Math.max(at, 3))}@${'•'.repeat(Math.max(dot === -1 ? domain.length : dot, 3))}${tld}`;
};
const maskPhone = (phone) => {
  if (!phone) return '—';
  const last = phone.slice(-2);
  return `${'•'.repeat(Math.max(phone.length - 2, 3))}${last}`;
};

// requirement.min/max_experience_years are actually sent in months despite
// the field name (confirmed against real data: a "Senior Level" role's
// max_experience_years of 120 is nonsensical as years but is exactly 10
// years in months) — converted here rather than at the call site so every
// reader of experienceRange gets a real year value.
const monthsToYears = (months) => Math.round(months / 12);
const formatExperienceRange = (minMonths, maxMonths) => {
  if (minMonths == null && maxMonths == null) return null;
  if (maxMonths == null) return `${monthsToYears(minMonths)}+ yrs`;
  if (minMonths == null) return `Up to ${monthsToYears(maxMonths)} yrs`;
  return `${monthsToYears(minMonths)} – ${monthsToYears(maxMonths)} yrs`;
};

// Confirmed in lakhs for one real record (expected_ctc: 24 -> ₹24 L, a
// sane Credit Manager salary) but another real record sent 3000000 for the
// same field — no Indian CTC realistically clears a few hundred lakhs, so
// anything past that is almost certainly the raw rupee amount and gets
// normalized down (3000000 -> 30, i.e. ₹30 L) rather than printed as-is.
// normalizeCtcToLpa (src/lib/utils.js) is the shared version of this same
// heuristic — UpdateProfileDetailsModal.jsx's own CTC prefill hit the exact
// same unit-ambiguity bug independently, so the logic now lives in one place.
const formatCtc = (value) => {
  const lakhs = normalizeCtcToLpa(value);
  return lakhs == null ? '—' : `₹${lakhs} L`;
};

// Raw GET .../brief response -> the shape this page renders.
const mapBrief = (raw, currentUserEmail) => {
  const {
    requirement,
    candidate,
    match: rawMatch,
    interview,
    timeline,
    verdicts,
    requirement_interviews: siblings,
  } = raw;
  // A candidate can be scheduled for interview before the matching service
  // has scored them against this requirement — `match` comes back null
  // rather than an empty object. Every `match.*` read below already has its
  // own `|| …` fallback for a missing field; this just keeps that same
  // degrade working when the whole object is absent instead of throwing.
  const match = rawMatch || {};

  const tierMeta = getTierMeta(match.match_type);

  const interviewers = interview.interviewers || [];
  const viewerEntry = interviewers.find(
    (p) =>
      (p.email || '').toLowerCase() === (currentUserEmail || '').toLowerCase()
  );
  const others = interviewers.filter((p) => p !== viewerEntry);
  const viewerOnPanel = others.length !== interviewers.length;
  let panelLabel = null;
  if (interviewers.length > 0) {
    if (viewerOnPanel) {
      panelLabel = others.length
        ? `Panel: you and ${others.map((p) => p.name).join(', ')}`
        : 'Panel: just you';
    } else {
      panelLabel = `Panel: ${interviewers.map((p) => p.name).join(', ')}`;
    }
  }

  const modeKey = interview.mode === 'Online' ? 'video' : null;

  const cov = match.coverage || {};

  return {
    requirement: {
      requirementId: requirement.requirement_id,
      reg: requirement.registration_id,
      title: requirement.title,
      careerLevel: requirement.career_level,
      employmentType: (requirement.employment_types || []).join(', '),
      locations: requirement.locations || [],
      experienceRange: formatExperienceRange(
        requirement.min_experience_years,
        requirement.max_experience_years
      ),
      closingDate: requirement.closing_date,
    },
    candidate: {
      candidateId: candidate.candidate_id,
      registrationId: candidate.registration_id,
      name: candidate.name,
      initials: (candidate.name || '')
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join(''),
      avatarTone: avatarToneFor(candidate.candidate_id),
      title: candidate.title,
      company: candidate.current_company,
      location: candidate.location,
      email: candidate.email,
      phone: candidate.phone,
      emailMask: maskEmail(candidate.email),
      phoneMask: maskPhone(candidate.phone),
      facts: [
        { value: match.total_experience || '—', label: 'Total exp' },
        { value: match.relevant_experience || '—', label: 'Relevant exp' },
        {
          value:
            candidate.notice_period_days != null
              ? `${candidate.notice_period_days} days`
              : '—',
          label: 'Notice',
        },
        { value: formatCtc(candidate.expected_ctc), label: 'Expected CTC' },
        {
          value:
            candidate.resume_score != null
              ? `${candidate.resume_score} / 100`
              : '—',
          label: 'Resume score',
        },
      ],
    },
    hasMatch: !!rawMatch,
    score: match.match_score,
    confidence: match.confidence,
    tier: tierMeta.key,
    tierLabel: tierMeta.label,
    tierRing: tierMeta.ring,
    tierBadgeClass: tierMeta.badgeClass,
    rank: match.rank,
    rankedCandidates: match.ranked_candidates,
    scoredOn: match.scored_at
      ? format(new Date(match.scored_at), 'd MMM')
      : '—',
    insight: match.scorers_read,
    scoreBreakdown: (match.score_breakdown || []).map((b) => ({
      key: b.key,
      label: b.label,
      value: b.value,
      available: b.available,
    })),
    coverage: {
      matched: cov.matched,
      partial: cov.partial,
      missing: cov.missing,
      requiredTotal: cov.required_total,
      matchedRequired: cov.matched_required,
      partialRequired: cov.partial_required,
    },
    skillMap: (match.fit_map || []).map((s) => ({
      name: s.name,
      required: s.required,
      status: s.status,
      ev: s.evidence_months,
      via: s.via,
      credit: s.credit,
    })),
    interview: {
      when: interview.interview_datetime
        ? format(new Date(interview.interview_datetime), 'EEE d MMM · h:mm a')
        : '—',
      startsAt: interview.interview_datetime
        ? new Date(interview.interview_datetime)
        : null,
      modeKey,
      modeLabel: modeKey ? 'Video' : interview.mode,
      round:
        interview.total_rounds != null
          ? `Round ${interview.round_number} of ${interview.total_rounds}`
          : `Round ${interview.round_number}`,
      durationMinutes: interview.duration_minutes,
      panelLabel,
      isCancelled: interview.is_cancelled,
      // Same field name as the list API (UpcomingInterviews.jsx's
      // mapInterview reads the identical `join_link` key) — absent until a
      // meeting link has actually been generated for this interview.
      joinLink: interview.join_link,
      viewerInterviewerId: viewerEntry?.id ?? null,
      myVerdict: viewerEntry
        ? (() => {
            const v = (verdicts || []).find(
              (item) => item.interviewer_id === viewerEntry.id
            );
            if (!v) return null;
            return {
              recommendation: API_TO_RECOMMENDATION[v.final_verdict] || null,
              comments: v.comments || '',
              submittedAtLabel: v.submitted_at
                ? format(new Date(v.submitted_at), 'h:mm a')
                : null,
            };
          })()
        : null,
    },
    timeline: (timeline || []).map((t) => ({
      role: t.role,
      company: t.company,
      when: `${format(new Date(t.from_date), 'MMM yyyy')} – ${
        t.to_date ? format(new Date(t.to_date), 'MMM yyyy') : 'Present'
      }`,
      current: t.is_current,
      flags: (t.flags || []).map(formatTimelineFlag),
    })),
    siblingInterviews: siblings || [],
  };
};

// Raw GET .../questionnaire response -> question view-models, plus the two
// short "job" summary lists (Verify/Clarify) built straight off the same
// questions rather than a separate curated field.
const mapQuestionnaire = (raw, currentInterviewerId) => {
  const questions = (raw.questions || []).map((q) => {
    // Each question carries every interviewer's response — pick out this
    // viewer's own prior read (if any) to pre-fill their chip + note.
    const myResponse = (q.responses || []).find(
      (r) => r.interviewer_id === currentInterviewerId
    );
    return {
      // Real identifier — API calls, React keys, ratings/notes lookups,
      // jump-to-question (handleJump) all key off this, unchanged by the
      // displayNumber logic below.
      id: q.evidence_tags?.question_id || `q${q.sort_order}`,
      dbId: q.id,
      group: q.group,
      question: q.question,
      questionSummary: q.question_summary,
      sources: (q.evidence_tags?.srcs || []).map((s) => ({
        tone: s.c,
        text: s.t,
      })),
      rationale: q.description,
      model: q.model_answer,
      listenFor: q.listen_for || [],
      redFlags: q.red_flags || [],
      clarifications: q.clarifications || [],
      degraded: !!q.evidence_tags?.degraded,
      savedRating: myResponse ? API_TO_RATING[myResponse.read_inference] : null,
      savedNote: myResponse?.comments || '',
    };
  });

  // The "Q1"/"Q2"/... badge shown everywhere in this UI is computed here,
  // not read off the backend's own evidence_tags.question_id (or its
  // q${sort_order} fallback) — confirmed live that numbering restarts per
  // section (verify's own Q1..Qn, then clarify starts back at Q1 too), so
  // flattening both sections on screen produced duplicate/out-of-order
  // badges. Assigned in the exact order QUESTION_GROUPS renders its
  // sections (verify, then clarify) and each group's own existing question
  // order within that, so it always reads as one continuous sequence
  // regardless of whatever the backend's internal numbering does.
  const displayNumberById = {};
  let nextDisplayNumber = 1;
  QUESTION_GROUPS.forEach((group) => {
    questions
      .filter((q) => q.group === group.key)
      .forEach((q) => {
        displayNumberById[q.id] = nextDisplayNumber;
        nextDisplayNumber += 1;
      });
  });
  const questionsWithNumbers = questions.map((q) => ({
    ...q,
    displayNumber: displayNumberById[q.id],
  }));

  const jobItems = (group) =>
    questionsWithNumbers
      .filter((q) => q.group === group)
      .map((q) => ({
        text: q.questionSummary || q.question,
        sources: q.sources,
        questionId: q.id,
        displayNumber: q.displayNumber,
      }));

  return {
    status: raw.status,
    questions: questionsWithNumbers,
    verify: jobItems('verify'),
    clarify: jobItems('clarify'),
  };
};

function SourceChips({ sources }) {
  if (!sources?.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {sources.map((s) => (
        <span
          key={s.text}
          className={cn(
            'rounded border px-1.5 py-px text-[10px] font-semibold',
            SOURCE_TONE_CLASS[s.tone] || SOURCE_TONE_CLASS.role
          )}
        >
          {s.text}
        </span>
      ))}
    </span>
  );
}

function ContactBlock({ candidate, revealed, onReveal, onViewProfile }) {
  return (
    <div className="mt-3.5 flex flex-wrap items-center gap-4 border-t border-border pt-3.5">
      <span className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-mono text-[11.5px] font-semibold text-foreground-strong">
          {revealed ? candidate.email : candidate.emailMask}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-mono text-[11.5px] font-semibold text-foreground-strong">
          {revealed ? candidate.phone : candidate.phoneMask}
        </span>
      </span>
      {!revealed && (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={onReveal}
        >
          <EyeOff className="h-3.5 w-3.5" />
          Reveal contact
        </Button>
      )}
      <Button
        variant="outline"
        size="headerSm"
        className="ml-auto gap-1.5"
        onClick={onViewProfile}
      >
        <FileText className="h-3.5 w-3.5" />
        View profile
      </Button>
      <span
        className={cn(
          'basis-full text-[11px]',
          revealed ? 'text-emerald-600' : 'text-muted-foreground'
        )}
      >
        {revealed
          ? 'Contact revealed for this session.'
          : 'Contact hidden by default.'}
      </span>
    </div>
  );
}

function JobItem({ item, tone, onJump }) {
  return (
    <div className="flex items-start gap-2.5 py-2.5 first:pt-0 last:pb-0">
      <span
        className={cn(
          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
          tone === 'v' ? 'bg-emerald-500' : 'bg-amber-500'
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-medium leading-relaxed text-foreground-strong">
          {item.text}
        </p>
        <div className="mt-1">
          <SourceChips sources={item.sources} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onJump(item.questionId)}
        className="shrink-0 self-center rounded text-[11.5px] font-semibold text-primary hover:underline"
      >
        Q{item.displayNumber} →
      </button>
    </div>
  );
}

function SkillRow({ skill }) {
  const statusMeta = {
    matched: {
      label: 'Matched',
      dot: 'bg-emerald-500',
      text: 'text-emerald-700',
    },
    partial: { label: 'Partial', dot: 'bg-amber-500', text: 'text-amber-700' },
    missing: {
      label: 'Gap',
      dot: 'bg-muted-foreground/50',
      text: 'text-muted-foreground',
    },
  }[skill.status] || {
    label: skill.status,
    dot: 'bg-muted-foreground/50',
    text: 'text-muted-foreground',
  };
  return (
    <div className="flex flex-wrap items-center gap-2.5 border-t border-border py-2 first:border-t-0">
      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground-strong">
        {skill.name}
      </span>
      <span
        className={cn(
          'rounded px-1.5 py-px text-[9px] font-bold tracking-wide',
          skill.required
            ? 'border border-primary/25 bg-primary/10 text-primary'
            : 'border border-border bg-muted text-muted-foreground'
        )}
      >
        {skill.required ? 'REQ' : 'OPT'}
      </span>
      {skill.ev != null && (
        <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">
          {skill.ev} mo
        </span>
      )}
      <span
        className={cn(
          'ml-auto inline-flex items-center gap-1.5 text-[11.5px] font-semibold',
          statusMeta.text
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', statusMeta.dot)} />
        {statusMeta.label}
      </span>
      {skill.via && (
        <div className="basis-full pl-0.5 text-[11.5px] text-muted-foreground">
          via <b className="font-semibold text-foreground-muted">{skill.via}</b>
          {skill.credit != null ? ` · credit ${skill.credit}` : ''}
        </div>
      )}
    </div>
  );
}

// Chips-only at rest; picking one expands a comment box (grid-rows trick —
// animates from 0 to content height without knowing the height up front)
// with its own Save button. The chip stays switchable the whole time it's
// expanded, since nothing commits until Save actually fires the PUT.
function QuestionRating({ displayNumber, savedRating, savedNote, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [draftRating, setDraftRating] = useState(savedRating || null);
  const [draftNote, setDraftNote] = useState(savedNote || '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const activeRating = expanded ? draftRating : savedRating;

  const handleChipClick = (value) => {
    setFailed(false);
    setDraftRating(value);
    if (!expanded) setDraftNote(savedNote || '');
    setExpanded(true);
  };

  const handleSave = async () => {
    if (!draftRating) return;
    setSaving(true);
    setFailed(false);
    try {
      await onSave(draftRating, draftNote);
      setSaving(false);
      setExpanded(false);
    } catch {
      setSaving(false);
      setFailed(true);
    }
  };

  return (
    <div className="mt-3.5 border-t border-dashed border-border pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Your read
        </span>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(RATING_META).map(([value, meta]) => (
            <button
              key={value}
              type="button"
              onClick={() => handleChipClick(value)}
              aria-pressed={activeRating === value}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-primary',
                activeRating === value && meta.activeClass
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  activeRating === value
                    ? meta.dotClass
                    : 'bg-muted-foreground/40'
                )}
              />
              {meta.label}
            </button>
          ))}
        </div>
        {!expanded && savedRating && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
            <Check className="h-3 w-3" />
            Saved
          </span>
        )}
      </div>
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="mt-2.5 flex flex-col gap-2">
            <Textarea
              rows={2}
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              placeholder="Interview note — what did you hear?"
              aria-label={`Note for Q${displayNumber}`}
              className="resize-y text-[12.5px] focus-visible:ring-0"
            />
            <div className="flex items-center justify-between gap-2">
              {failed ? (
                <span className="text-[11px] font-medium text-red-600">
                  Couldn&apos;t save — try again.
                </span>
              ) : (
                <span />
              )}
              <Button
                size="sm"
                className="gap-1.5"
                onClick={handleSave}
                disabled={!draftRating || saving}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Skip isn't a read on the candidate's answer — it's "I'm not asking this
// one" — so it gets its own always-available action at the top of the card
// instead of living inside QuestionRating's Strong/Mixed/Weak chip row.
// Fires the exact same onSave(ratingValue, noteValue) the chips use, just
// with 'skip' and no note to capture, straight away — no draft/expand step.
function SkipAction({ onSkip }) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await onSkip();
      setSaving(false);
    } catch {
      setSaving(false);
      setFailed(true);
    }
  };

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 text-xs text-muted-foreground hover:text-foreground-strong"
        onClick={handleClick}
        disabled={saving}
      >
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <SkipForward className="h-3.5 w-3.5" />
        )}
        Skip
      </Button>
      {failed && (
        <span className="text-[10.5px] font-medium text-red-600">
          Couldn&apos;t save
        </span>
      )}
    </div>
  );
}

function QuestionCard({
  question,
  rating,
  note,
  pulsing,
  modelOpen,
  onToggleModel,
  guideOpen,
  onToggleGuide,
  onSave,
  cardRef,
  ratingLocked,
  ratingLockedLabel,
}) {
  // A skipped question stays skipped across reloads (it's just
  // scorecard.ratings[q.id] === 'skip', same as any other rating), but the
  // interviewer can still reopen it here to change their mind — this local
  // override is the only thing that distinguishes "collapsed" from "manually
  // reopened" once a question has a skip on record.
  const [forceExpanded, setForceExpanded] = useState(false);
  const isCollapsed = rating === 'skip' && !forceExpanded;

  return (
    <article
      ref={cardRef}
      id={`qc-${question.id}`}
      tabIndex={-1}
      className={cn(
        'mt-3 scroll-mt-20 rounded-lg border border-border p-4 first:mt-0',
        pulsing && 'ring-2 ring-primary ring-offset-2'
      )}
      aria-label={`Question ${question.displayNumber}`}
    >
      {/* Same 0fr/1fr grid-rows trick QuestionRating's own reveal uses —
          shrinks the full card down to nothing as the one-line summary
          below grows in, rather than an abrupt show/hide. */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: isCollapsed ? '0fr' : '1fr' }}
      >
        <div className="overflow-hidden">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 shrink-0 rounded border border-primary/25 bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] font-bold text-primary">
              Q{question.displayNumber}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-1.5">
                <p className="text-[13.5px] font-semibold leading-relaxed text-foreground-strong">
                  {question.question}
                </p>
                {question.degraded && (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger asChild>
                        <span className="mt-1 flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-dashed border-border text-[9px] font-bold text-muted-foreground">
                          ?
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Generated with limited data — treat as a starting point.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <SourceChips sources={question.sources} />
              </div>
              <p className="mt-2 max-w-[78ch] text-xs text-muted-foreground">
                {question.rationale}
              </p>
              <button
                type="button"
                onClick={onToggleModel}
                aria-expanded={modelOpen}
                className="mt-2 mr-2 inline-flex items-center gap-1.5 rounded text-xs font-semibold text-primary"
              >
                <ChevronRight
                  className={cn(
                    'h-3 w-3 transition-transform',
                    modelOpen && 'rotate-90'
                  )}
                />
                {modelOpen ? 'Hide model answer' : 'Show model answer'}
              </button>
              {modelOpen && (
                <div className="mt-1.5 rounded-r-md border-l-[3px] border-primary/30 bg-primary/5 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
                  {question.model}
                </div>
              )}
              <button
                type="button"
                onClick={onToggleGuide}
                aria-expanded={guideOpen}
                className="mt-2 inline-flex items-center gap-1.5 rounded text-xs font-semibold text-primary"
              >
                <ChevronRight
                  className={cn(
                    'h-3 w-3 transition-transform',
                    guideOpen && 'rotate-90'
                  )}
                />
                {guideOpen ? 'Hide scoring guide' : 'Show scoring guide'}
              </button>
              {guideOpen && (
                <>
                  <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <h4 className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700">
                        Listen for
                      </h4>
                      <ul className="flex flex-col gap-1">
                        {question.listenFor.map((l) => (
                          <li
                            key={l}
                            className="flex items-start gap-1.5 text-xs text-muted-foreground"
                          >
                            <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-red-600">
                        Red flags
                      </h4>
                      <ul className="flex flex-col gap-1">
                        {question.redFlags.map((l) => (
                          <li
                            key={l}
                            className="flex items-start gap-1.5 text-xs text-muted-foreground"
                          >
                            <X className="mt-0.5 h-3 w-3 shrink-0 text-red-500" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {question.clarifications?.length > 0 && (
                    <div className="mt-3">
                      <h4 className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        If they&rsquo;re stuck, prompt with
                      </h4>
                      <ul className="flex flex-col gap-1">
                        {question.clarifications.map((c) => (
                          <li
                            key={c}
                            className="flex items-start gap-1.5 text-xs text-muted-foreground"
                          >
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {ratingLocked ? (
                <div className="mt-3.5 flex items-center gap-2 border-t border-dashed border-border pt-3 text-[11.5px] text-muted-foreground">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  {ratingLockedLabel}
                </div>
              ) : (
                <QuestionRating
                  displayNumber={question.displayNumber}
                  savedRating={rating}
                  savedNote={note}
                  onSave={(nextRating, nextNote) =>
                    onSave(nextRating, nextNote)
                  }
                />
              )}
            </div>
            {!ratingLocked && (
              <SkipAction
                onSkip={async () => {
                  await onSave('skip', '');
                  // Always collapse on a successful skip, including a
                  // re-skip after the interviewer manually reopened a
                  // previously-skipped question — otherwise forceExpanded
                  // stays true from the reopen and the card never shrinks
                  // back down.
                  setForceExpanded(false);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* The collapsed one-liner — mirror image of the block above: 1fr
          when skipped, 0fr otherwise, so the two swap places instead of
          one just vanishing while the other pops in. */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: isCollapsed ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <button
            type="button"
            onClick={() => setForceExpanded(true)}
            className="flex w-full items-center gap-2.5 text-left"
          >
            <span className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-bold text-muted-foreground">
              Q{question.displayNumber}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">
              {question.questionSummary || question.question}
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground">
              <SkipForward className="h-3 w-3" />
              Skipped
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </div>
      </div>
    </article>
  );
}

function LoadingPlaceholder({ label }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center mt-4 text-[13px] text-muted-foreground">
      {label}
    </div>
  );
}

// iconRetry swaps the "Try again" text button for a reload icon button
// (spinning while `retrying`), for states where the label already says it all.
function ErrorPlaceholder({
  label,
  onRetry,
  iconRetry = false,
  retrying = false,
}) {
  return (
    <div className="rounded-xl mt-4 border border-border bg-card p-8 text-center">
      <p className="text-[13.5px] text-foreground-strong">{label}</p>
      {iconRetry ? (
        <Button
          variant="outline"
          size="icon"
          className="group mt-4 h-8 w-8 border-primary/30 bg-primary/5 text-primary shadow-sm transition-all hover:-translate-y-px hover:border-primary/50 hover:bg-primary/10 hover:text-primary hover:shadow-md active:translate-y-0"
          onClick={onRetry}
          disabled={retrying}
          aria-label="Reload"
          title="Reload"
        >
          <RefreshCw
            className={cn(
              'h-4 w-4 transition-transform duration-500',
              retrying ? 'animate-spin' : 'group-hover:rotate-180'
            )}
          />
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// Collapses the loading/error/not-ready/empty/content five-way branch the
// question bank needs without nesting ternaries.
function QuestionnaireSection({ query, status, isEmpty, children }) {
  if (query.isLoading) {
    return (
      <div className="mt-4">
        <LoadingPlaceholder label="Loading question bank…" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="mt-4">
        <ErrorPlaceholder
          label="Couldn't load the question bank."
          onRetry={query.refetch}
        />
      </div>
    );
  }
  // Zero questions back — checked before the status gate because the API
  // reports an empty questionnaire as "pending", which would otherwise
  // always show "still being generated" and never reach this state. Reuses
  // ErrorPlaceholder (with its reload-icon variant) since the fix is the same
  // action (refetch), even though this isn't technically a query error.
  if (isEmpty) {
    return (
      <div className="mt-4">
        <ErrorPlaceholder
          label="No questions were generated for this interview yet."
          onRetry={query.refetch}
          iconRetry
          retrying={query.isFetching}
        />
      </div>
    );
  }
  if (status !== 'ready') {
    return (
      <div className="mt-4">
        <LoadingPlaceholder label="Questions are still being generated for this interview." />
      </div>
    );
  }
  return children;
}

// Same loading/error/empty/content branch shape as QuestionnaireSection
// above, for the Job description tab's own query.
function JobDescriptionSection({
  query,
  jobDescription,
  additionalRequirements,
}) {
  if (query.isLoading) {
    return <LoadingPlaceholder label="Loading job description…" />;
  }
  if (query.isError) {
    return (
      <ErrorPlaceholder
        label="Couldn't load the job description."
        onRetry={query.refetch}
      />
    );
  }
  if (!jobDescription && !additionalRequirements) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
        No detailed job description was added for this requirement.
      </div>
    );
  }
  return (
    <section className="mt-4 rounded-xl border border-border bg-card p-5">
      {jobDescription && <MarkdownViewer content={jobDescription} />}
      {additionalRequirements && (
        <div className="mt-3.5 border-t border-dashed border-border pt-3.5">
          <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Additional requirements
          </h3>
          <MarkdownViewer content={additionalRequirements} />
        </div>
      )}
    </section>
  );
}

// The final-call modal — recommendation + summary comment, POSTed as one
// scorecard verdict. Self-contained draft/submitting/error state, same
// pattern as QuestionRating's own Save flow.
function VerdictModal({
  open,
  onOpenChange,
  candidateName,
  ratedCount,
  totalCount,
  initialRecommendation,
  initialComments,
  onSubmit,
}) {
  const [recommendation, setRecommendation] = useState(
    initialRecommendation || null
  );
  const [comments, setComments] = useState(initialComments || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Which tile's icon should replay the pop-in animation — cleared once the
  // animation finishes so clicking the same tile twice still replays it.
  const [justPicked, setJustPicked] = useState(null);

  useEffect(() => {
    if (open) {
      setRecommendation(initialRecommendation || null);
      setComments(initialComments || '');
      setError(null);
      setJustPicked(null);
    }
  }, [open, initialRecommendation, initialComments]);

  const handleSubmit = async () => {
    if (!recommendation) {
      setError('Pick a recommendation to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(recommendation, comments);
      setSubmitting(false);
    } catch {
      setSubmitting(false);
      setError("Couldn't submit — try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !submitting && onOpenChange(next)}
    >
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Submit scorecard</DialogTitle>
          <DialogDescription>
            {candidateName ? `Final call for ${candidateName} — ` : ''}
            {ratedCount} / {totalCount} questions rated.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Recommendation
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(REC_META).map(([value, meta]) => {
                const Icon = REC_ICONS[value];
                const active = recommendation === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setRecommendation(value);
                      setError(null);
                      setJustPicked(value);
                    }}
                    aria-pressed={active}
                    className={cn(
                      'flex flex-col items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-3.5 text-center transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:shadow-sm active:translate-y-0 active:scale-95',
                      active && meta.activeClass
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5',
                        justPicked === value && 'animate-pop'
                      )}
                      onAnimationEnd={() => setJustPicked(null)}
                    />
                    <span className="text-[12.5px] font-semibold">
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Comments
            </div>
            <Textarea
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Summary for the record — what tipped the call?"
              aria-label="Scorecard comments"
              className="resize-y text-[13px]"
            />
          </div>
          {error && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] font-medium text-amber-700"
              role="alert"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="gap-1.5"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Submit scorecard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shown once VerdictModal's submit actually succeeds — replaces the old
// plain toast-only confirmation with a real "what now" moment: the
// interviewer's done with this candidate, so the one action offered is
// straight back to the upcoming-interviews list rather than leaving them on
// a now-fully-submitted brief with nothing left to do here.
function VerdictSuccessDialog({ open, onOpenChange, candidateName, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Scorecard submitted</DialogTitle>
          <DialogDescription>
            {candidateName
              ? `Your final call for ${candidateName} has been recorded.`
              : 'Your final call has been recorded.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col items-center gap-2 py-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ds-success-subtle text-ds-success">
            <CheckCircle2 className="h-6 w-6" strokeWidth={2} />
          </div>
        </DialogBody>
        <DialogFooter className="justify-center">
          <Button className="w-full gap-1.5" onClick={onDone}>
            Back to upcoming interviews
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Collapses the loading/error/ready branch for the sticky note's body into a
// plain value instead of a nested ternary — same reasoning as
// QuestionnaireSection's own split above. Returns `null` once notes are
// loaded, which MarkdownEditor's `variant="sticky"` reads as "no override,
// show the real editor" (see its own `children || editorBody` check) — a
// wrapping component here instead of a plain function would always be a
// truthy element regardless of what it renders internally, breaking that
// check, so this stays a function returning a real value.
function renderNotesBodyOverride(query) {
  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorPlaceholder
        label="Couldn't load your notes."
        onRetry={query.refetch}
      />
    );
  }
  return null;
}

function NotesSaveStatus({ saveError, justSaved }) {
  if (saveError) {
    return (
      <span className="text-[11px] font-medium text-red-600">
        Couldn&apos;t save — try again.
      </span>
    );
  }
  if (justSaved) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  return <span />;
}

// Floating note for the interviewer's own private notes on this interview —
// opened via the header's Notes button, closable either from there or from
// its own footer's Close button (`onClose`, wired to the same `notesOpen`
// state). Stays mounted at all times once opened the first time, just
// hidden via CSS, so an in-progress draft survives a close/reopen instead
// of resetting. Still no separate title bar or color picker — the editor
// itself (toolbar/editable area/footer, same neutral styling the inline
// variant uses) is the whole widget, just pinned to the viewport via
// MarkdownEditor's `variant="sticky"`. The footer below (`NotesSaveStatus`
// + Close/Save buttons) renders inside MarkdownEditor's own footer bar, not
// a separate wrapper, so it reads as one continuous card instead of
// floating loose over page content. The API's `comments` field is real
// markdown source, not HTML — same markdownToHTML/htmlToMarkdown
// round-trip MarkdownEditor's other call sites use to bridge its
// contentEditable HTML to a markdown-shaped field.
function NotesSticky({ open, onClose, interviewRoundDetailId, onDirtyChange }) {
  // Stays true forever once the widget has been opened once — keeps the
  // fetch from firing before the interviewer ever asks for their notes, but
  // (unlike `open`) never flips back off, so minimizing doesn't re-trigger it.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  useEffect(() => {
    if (open) setHasOpenedOnce(true);
  }, [open]);

  const notesQuery = useInterviewNotes(interviewRoundDetailId, hasOpenedOnce);
  const saveMutation = useSaveInterviewNotes();

  const [draftHtml, setDraftHtml] = useState('');
  const [savedHtml, setSavedHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Pre-fills the editor from the fetched value exactly once per interview
  // (`syncedRef`) — after that, local state is the only source of truth so a
  // slow background refetch never clobbers text mid-edit.
  const syncedRef = useRef(false);
  useEffect(() => {
    syncedRef.current = false;
    setDraftHtml('');
    setSavedHtml('');
    setSaveError(false);
    setJustSaved(false);
  }, [interviewRoundDetailId]);
  useEffect(() => {
    if (!notesQuery.data || syncedRef.current) return;
    const html = markdownToHTML(notesQuery.data.comments || '');
    setDraftHtml(html);
    setSavedHtml(html);
    syncedRef.current = true;
  }, [notesQuery.data]);

  const dirty = draftHtml !== savedHtml;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      await saveMutation.mutateAsync({
        interviewRoundDetailId,
        payload: { comments: htmlToMarkdown(draftHtml) },
      });
      setSavedHtml(draftHtml);
      setSaving(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch {
      setSaving(false);
      setSaveError(true);
    }
  };

  if (!hasOpenedOnce) return null;

  return (
    <MarkdownEditor
      variant="sticky"
      value={draftHtml}
      onChange={setDraftHtml}
      minHeight={200}
      maxHeight={340}
      title="Interview notes"
      open={open}
      footer={
        <>
          <NotesSaveStatus saveError={saveError} justSaved={justSaved} />
          <div className="flex items-center gap-2">
            {/* onMouseDown preventDefault — same trick MarkdownEditor's own
                toolbar buttons use — stops these from blurring the
                contentEditable area. Without it, clicking Close/Save right
                after typing blurs the note first, which collapses the
                now-focus-gated toolbar and shifts this whole footer up
                mid-click, so the click lands on empty space instead of the
                button and the save/close silently doesn't fire. */}
            <Button
              variant="outline"
              size="sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </Button>
          </div>
        </>
      }
    >
      {renderNotesBodyOverride(notesQuery)}
    </MarkdownEditor>
  );
}

function InterviewBrief() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { interviewRoundDetailId } = useParams();
  const { getUserInfo } = useAuth();
  const currentUserEmail = getUserInfo()?.email;

  const idNum = Number(interviewRoundDetailId);

  const briefQuery = useInterviewBrief(idNum);
  const questionnaireQuery = useInterviewQuestionnaire(idNum);
  const submitResponseMutation = useSubmitQuestionResponse();
  const submitVerdictMutation = useSubmitVerdict();

  const brief = useMemo(
    () =>
      briefQuery.data ? mapBrief(briefQuery.data, currentUserEmail) : null,
    [briefQuery.data, currentUserEmail]
  );
  const viewerInterviewerId = brief?.interview.viewerInterviewerId ?? null;
  const questionnaire = useMemo(
    () =>
      questionnaireQuery.data
        ? mapQuestionnaire(questionnaireQuery.data, viewerInterviewerId)
        : null,
    [questionnaireQuery.data, viewerInterviewerId]
  );

  const [scorecards, setScorecards] = useState({});
  const [openSections, setOpenSections] = useState({
    fit: false,
    timeline: false,
    verify: true,
    clarify: true,
  });
  const [openModelAnswers, setOpenModelAnswers] = useState({});
  const [openGuides, setOpenGuides] = useState({});
  const [pendingJump, setPendingJump] = useState(null);
  const [pulseId, setPulseId] = useState(null);
  const [verdictModalOpen, setVerdictModalOpen] = useState(false);
  const [verdictSuccessOpen, setVerdictSuccessOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('brief');
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);

  // Lazy — only fetches once the interviewer actually opens the Job
  // description tab, same reasoning as this page's other on-visit gating.
  const jdQuery = useOpportunityDetails(brief?.requirement.requirementId, {
    enabled: activeTab === 'jd',
  });
  const jobDescription = jdQuery.data?.data?.detailed_job_description;
  const additionalRequirements = jdQuery.data?.data?.additional_requirements;

  const questionRefs = useRef({});

  // Reset per-visit UI state (not the scorecard itself — that stays cached
  // per interview id) whenever the pager navigates to a different interview.
  useEffect(() => {
    setPendingJump(null);
    setPulseId(null);
    setOpenModelAnswers({});
  }, [idNum]);

  const scorecard = scorecards[idNum] || EMPTY_SCORECARD;

  useEffect(() => {
    if (!pendingJump) return undefined;
    const el = questionRefs.current[pendingJump];
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
    setPulseId(pendingJump);
    setPendingJump(null);
    const t = setTimeout(() => setPulseId(null), 1600);
    return () => clearTimeout(t);
  }, [pendingJump]);

  const updateScorecard = (updater) => {
    setScorecards((prev) => ({
      ...prev,
      [idNum]: updater(prev[idNum] || EMPTY_SCORECARD),
    }));
  };

  // Pre-fill this viewer's own prior reads/verdict from the server (the
  // questionnaire's per-question `responses[]` and the brief's `verdicts[]`,
  // both already filtered to this interviewer during mapping) — local state
  // still wins for anything already tracked this session, so a save made a
  // moment ago is never clobbered by the refetch it triggers.
  useEffect(() => {
    if (!questionnaire && !brief) return;
    updateScorecard((prev) => {
      const ratings = { ...prev.ratings };
      const notes = { ...prev.notes };
      (questionnaire?.questions || []).forEach((q) => {
        if (q.savedRating && !(q.id in ratings)) ratings[q.id] = q.savedRating;
        if (q.savedNote && !(q.id in notes)) notes[q.id] = q.savedNote;
      });
      const myVerdict = brief?.interview.myVerdict;
      return {
        ...prev,
        ratings,
        notes,
        recommendation:
          prev.recommendation || myVerdict?.recommendation || null,
        verdictComments: prev.verdictComments || myVerdict?.comments || '',
        submittedAt: prev.submittedAt || myVerdict?.submittedAtLabel || null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire, brief, idNum]);

  const handleJump = (questionId) => {
    const q = questionnaire?.questions.find((item) => item.id === questionId);
    if (q) setOpenSections((prev) => ({ ...prev, [q.group]: true }));
    setPendingJump(questionId);
  };

  // Awaited by QuestionRating's own Save button — a rejection here leaves
  // that question's editor open with an inline error instead of silently
  // marking it rated.
  const handleSaveQuestionResponse = async (
    question,
    ratingValue,
    noteValue
  ) => {
    await submitResponseMutation.mutateAsync({
      interviewRoundDetailId: idNum,
      questionnaireId: question.dbId,
      payload: {
        read_inference: RATING_TO_API[ratingValue],
        comments: noteValue,
      },
    });
    updateScorecard((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, [question.id]: ratingValue },
      notes: { ...prev.notes, [question.id]: noteValue },
    }));
  };

  const handleReveal = () => {
    updateScorecard((prev) => ({ ...prev, contactRevealed: true }));
    toast({
      message:
        'Contact revealed for this session (submission not wired up yet)',
    });
  };

  // Awaited by VerdictModal's own Submit button — a rejection here leaves
  // the modal open with an inline error instead of silently closing.
  const handleVerdictSubmit = async (recommendationValue, commentsValue) => {
    await submitVerdictMutation.mutateAsync({
      interviewRoundDetailId: idNum,
      payload: {
        final_verdict: RECOMMENDATION_TO_API[recommendationValue],
        comments: commentsValue,
      },
    });
    const now = new Date();
    const stamp = format(now, 'h:mm a');
    updateScorecard((prev) => ({
      ...prev,
      recommendation: recommendationValue,
      verdictComments: commentsValue,
      submittedAt: stamp,
    }));
    setVerdictModalOpen(false);
    setVerdictSuccessOpen(true);
  };

  const handleBackToUpcoming = () => {
    setVerdictSuccessOpen(false);
    navigate('/interviews/upcoming');
  };

  const siblings = brief?.siblingInterviews || [];
  const pagerIndex = siblings.findIndex(
    (s) => s.interview_round_detail_id === idNum
  );

  const goToInterview = (targetId) => {
    navigate(`/interviews/${targetId}/brief`);
    window.scrollTo({ top: 0 });
  };

  if (briefQuery.isLoading) {
    return (
      <div>
        <PageHeader
          breadcrumbs={[
            { label: 'Interviews', href: '/interviews/upcoming' },
            { label: 'Interview brief' },
          ]}
        />
        <PageContainer>
          <LoadingPlaceholder label="Loading interview brief…" />
        </PageContainer>
      </div>
    );
  }

  if (briefQuery.isError || !brief) {
    return (
      <div>
        <PageHeader
          breadcrumbs={[
            { label: 'Interviews', href: '/interviews/upcoming' },
            { label: 'Interview brief' },
          ]}
        />
        <PageContainer>
          <ErrorPlaceholder
            label="Couldn't load this interview brief."
            onRetry={() => briefQuery.refetch()}
          />
        </PageContainer>
      </div>
    );
  }

  const { requirement, candidate } = brief;
  const cov = brief.coverage;
  const mPct = Math.round((cov.matchedRequired / cov.requiredTotal) * 100);
  const pPct = Math.round((cov.partialRequired / cov.requiredTotal) * 100);
  const gapsReq = cov.requiredTotal - cov.matchedRequired - cov.partialRequired;

  const allQuestions = questionnaire?.questions || [];
  const verifyJobs = questionnaire?.verify || [];
  const clarifyJobs = questionnaire?.clarify || [];
  // Skipped isn't a read, so it doesn't count toward "rated" — the footer
  // already shows it as its own number alongside this one. Submitting still
  // only needs every question handled one way or another, so the gate below
  // sums both rather than requiring a real read on a question that was
  // deliberately skipped.
  const totalRated = allQuestions.filter(
    (q) => scorecard.ratings[q.id] && scorecard.ratings[q.id] !== 'skip'
  ).length;
  const totalSkipped = allQuestions.filter(
    (q) => scorecard.ratings[q.id] === 'skip'
  ).length;
  const totalHandled = totalRated + totalSkipped;
  const allRated =
    allQuestions.length > 0 && totalHandled >= allQuestions.length;
  // Rating before the interview has actually happened isn't a real read —
  // there's nothing to score yet. Fails open (treated as started) when the
  // interview has no known time rather than silently locking the page.
  const interviewStarted =
    !brief.interview.startsAt ||
    brief.interview.startsAt.getTime() <= Date.now();
  const ratingLockedLabel = `Rating opens once the interview starts — ${brief.interview.when}.`;

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Interviews', href: '/interviews/upcoming' },
          { label: 'Interview brief' },
        ]}
        actions={
          <>
            {brief?.interview.joinLink && (
              <Button
                size="headerSm"
                className="gap-1.5"
                onClick={() =>
                  window.open(
                    brief.interview.joinLink,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                <Video className="h-3.5 w-3.5" strokeWidth={1.8} />
                Join call
              </Button>
            )}
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => setNotesOpen((prev) => !prev)}
            >
              <StickyNote className="h-3.5 w-3.5" strokeWidth={1.8} />
              Notes
              {notesDirty && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-sky-500"
                  aria-hidden="true"
                />
              )}
            </Button>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => window.print()}
            >
              <Printer className="h-3.5 w-3.5" strokeWidth={1.8} />
              Print brief
            </Button>
          </>
        }
      />
      <PageContainer>
        <div className="flex flex-wrap items-start gap-5">
          <div className="min-w-[280px] flex-1">
            <h1 className="text-[20px] font-bold tracking-tight text-foreground-strong">
              Interview brief
            </h1>
            <p className="mt-1 max-w-[74ch] text-[13.5px] text-muted-foreground">
              Who they are, what got them shortlisted, and what this interview
              has to verify and clarify — in one read. Rate as you go; your
              scorecard feeds the pipeline.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded border border-primary/20 bg-primary/10 px-2 py-0.5 font-mono text-[11.5px] font-semibold text-primary">
                {requirement.reg}
              </span>
              <span className="rounded border border-border bg-muted px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                <b className="font-semibold text-foreground-strong">
                  {requirement.title}
                </b>{' '}
                · {requirement.employmentType}
              </span>
              {requirement.locations.length > 0 && (
                <span className="rounded border border-border bg-muted px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                  {requirement.locations.join(', ')}
                </span>
              )}
              {requirement.experienceRange && (
                <span className="rounded border border-border bg-muted px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                  {requirement.experienceRange}
                </span>
              )}
            </div>
          </div>
          {siblings.length > 1 && (
            <div
              className="flex items-center gap-2 self-center"
              role="group"
              aria-label="Scheduled interviews for this requirement"
            >
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={pagerIndex <= 0}
                onClick={() =>
                  goToInterview(
                    siblings[pagerIndex - 1].interview_round_detail_id
                  )
                }
                aria-label="Previous candidate"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="whitespace-nowrap font-mono text-[11.5px] font-semibold text-muted-foreground">
                {pagerIndex + 1} of {siblings.length}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={
                  pagerIndex === -1 || pagerIndex >= siblings.length - 1
                }
                onClick={() =>
                  goToInterview(
                    siblings[pagerIndex + 1].interview_round_detail_id
                  )
                }
                aria-label="Next candidate"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4">
          <FilterButton
            filters={[
              { label: 'Brief', value: 'brief' },
              { label: 'Job description', value: 'jd' },
            ]}
            defaultActiveFilter={activeTab}
            onFilterChange={setActiveTab}
          />
        </div>

        {activeTab === 'brief' && (
          <>
            {/* Identity */}
            <section
              className="mt-4 rounded-xl border border-border bg-card p-5"
              aria-label="Candidate identity"
            >
              <div className="flex flex-wrap items-start gap-4">
                <div
                  className={cn(
                    'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold',
                    AVATAR_TONE_CLASS[candidate.avatarTone]
                  )}
                  aria-hidden="true"
                >
                  {candidate.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-[17px] font-bold tracking-tight text-foreground-strong">
                      {candidate.name}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                        brief.tierBadgeClass
                      )}
                    >
                      {brief.tierLabel}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[13px] text-muted-foreground">
                    {candidate.title} · {candidate.company} ·{' '}
                    {candidate.location} · Rank{' '}
                    <b className="font-semibold text-foreground-strong">
                      #{brief.rank}
                    </b>
                    {brief.rankedCandidates
                      ? ` of ${brief.rankedCandidates}`
                      : ''}{' '}
                    on this requirement
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-3.5">
                  <ScoreRing
                    score={brief.score}
                    color={brief.tierRing}
                    size={52}
                  />
                  <div className="text-left">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Match score
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                      confidence{' '}
                      {brief.confidence != null
                        ? brief.confidence.toFixed(2)
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3.5 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <b className="font-semibold text-foreground-strong">
                    {brief.interview.when}
                  </b>
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  {brief.interview.modeKey && (
                    <Video className="h-3 w-3 text-muted-foreground" />
                  )}
                  {brief.interview.modeLabel}
                </span>
                {brief.interview.panelLabel && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                    <UserCheck className="h-3 w-3 text-muted-foreground" />
                    {brief.interview.panelLabel}
                  </span>
                )}
              </div>

              <div className="mt-3.5 flex flex-wrap overflow-hidden rounded-lg border border-border">
                {candidate.facts.map((f) => (
                  <div
                    key={f.label}
                    className="min-w-[110px] flex-1 border-r border-border px-3.5 py-2.5 last:border-r-0"
                  >
                    <div
                      className={cn(
                        'font-mono text-sm font-bold',
                        f.value === '—'
                          ? 'text-muted-foreground/50'
                          : 'text-foreground-strong'
                      )}
                    >
                      {f.value}
                    </div>
                    <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {f.label}
                    </div>
                  </div>
                ))}
              </div>

              {brief.insight && (
                <div className="mt-3.5 flex items-start gap-2.5 rounded-lg border border-primary/15 bg-primary/5 p-3">
                  <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <p className="text-[12.5px] text-muted-foreground">
                    <b className="font-semibold text-foreground-strong">
                      Scorer&rsquo;s read:{' '}
                    </b>
                    {brief.insight}
                  </p>
                </div>
              )}

              <ContactBlock
                candidate={candidate}
                revealed={scorecard.contactRevealed}
                onReveal={handleReveal}
                onViewProfile={() => setProfileModalOpen(true)}
              />
            </section>

            {/* Why the shortlist */}
            <section
              className="mt-4 rounded-xl border border-border bg-card p-5"
              aria-label="Why the shortlist"
            >
              <h2 className="text-[15px] font-bold tracking-tight text-foreground-strong">
                Why the shortlist
              </h2>
              {!brief.hasMatch ? (
                <p className="mt-2 text-[13px] text-muted-foreground">
                  This candidate hasn&apos;t been scored against this
                  requirement yet — there&apos;s no shortlist breakdown to show.
                  The questions below still cover the role&apos;s must-haves.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Scored by the matching service on{' '}
                    <b className="font-semibold text-foreground-strong">
                      {brief.scoredOn}
                    </b>
                    . This brief never re-scores — it exists to{' '}
                    <b className="font-semibold text-foreground-strong">
                      verify
                    </b>{' '}
                    what the scorer credited and{' '}
                    <b className="font-semibold text-foreground-strong">
                      clarify
                    </b>{' '}
                    what it could not see.
                  </p>
                  <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <div className="flex items-baseline justify-between text-[12.5px] font-semibold text-foreground-strong">
                        <span>Must-have coverage</span>
                        <span className="font-mono">
                          {cov.matchedRequired} / {cov.requiredTotal}
                        </span>
                      </div>
                      <div
                        className="mt-2 flex h-2 overflow-hidden rounded-full bg-muted"
                        role="img"
                        aria-label={`Must-have skills: ${cov.matchedRequired} matched, ${cov.partialRequired} partial, ${gapsReq} gaps of ${cov.requiredTotal}`}
                      >
                        <span
                          className="h-full bg-emerald-500"
                          style={{ width: `${mPct}%` }}
                        />
                        <span
                          className="h-full bg-amber-500"
                          style={{ width: `${pPct}%` }}
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3.5 text-[11.5px] text-muted-foreground">
                        <span>
                          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />
                          Matched{' '}
                          <span className="font-mono font-semibold text-foreground-strong">
                            {cov.matchedRequired}
                          </span>
                        </span>
                        <span>
                          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500" />
                          Partial{' '}
                          <span className="font-mono font-semibold text-foreground-strong">
                            {cov.partialRequired}
                          </span>
                        </span>
                        <span>
                          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-muted-foreground/40" />
                          Gaps{' '}
                          <span className="font-mono font-semibold text-foreground-strong">
                            {gapsReq}
                          </span>
                        </span>
                      </div>
                      <p className="mt-3 text-xs text-muted-foreground">
                        All {brief.skillMap.length} requirement skills:{' '}
                        {cov.matched} matched · {cov.partial} partial ·{' '}
                        {cov.missing} gap
                        {cov.missing === 1 ? '' : 's'}. The fit map below shows
                        how each partial was credited.
                      </p>
                    </div>
                    <div
                      role="group"
                      aria-label="Score components"
                      className="flex flex-col gap-2"
                    >
                      {brief.scoreBreakdown.map((b) => (
                        <div
                          key={b.key}
                          className="grid grid-cols-[110px_1fr_34px] items-center gap-2.5"
                        >
                          <span className="text-xs font-medium text-muted-foreground">
                            {b.label}
                          </span>
                          {b.available ? (
                            <>
                              <span className="h-[7px] overflow-hidden rounded-full bg-muted">
                                <span
                                  className="block h-full rounded-full bg-primary"
                                  style={{ width: `${b.value}%` }}
                                />
                              </span>
                              <span className="text-right font-mono text-[11.5px] font-bold text-foreground-strong">
                                {b.value}
                              </span>
                            </>
                          ) : (
                            <>
                              <TooltipProvider>
                                <Tooltip delayDuration={150}>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex w-fit cursor-help items-center rounded-full border border-dashed border-border px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground">
                                      Not available
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    No data yet for this — it isn&apos;t counted
                                    in the match score until there is.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <span className="text-right font-mono text-[11.5px] font-semibold text-muted-foreground/50">
                                —
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </section>

            {/* Fit map */}
            <Collapsible
              open={openSections.fit}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, fit: open }))
              }
            >
              <section className="mt-4 rounded-xl border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2.5 px-5 py-4 text-left"
                    aria-expanded={openSections.fit}
                  >
                    <ClipboardList className="h-4 w-4 text-primary" />
                    <span className="text-sm font-bold text-foreground-strong">
                      Fit map — skills vs the requirement
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                      {cov.matched} · {cov.partial} · {cov.missing}
                    </span>
                    <ChevronDown
                      className={cn(
                        'ml-auto h-4 w-4 text-muted-foreground transition-transform',
                        openSections.fit && 'rotate-180'
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-5 pb-5">
                  <div className="mb-1 mt-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Must-have
                  </div>
                  {brief.skillMap
                    .filter((s) => s.required)
                    .map((s) => (
                      <SkillRow key={s.name} skill={s} />
                    ))}
                  <div className="mb-1 mt-3.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Nice-to-have
                  </div>
                  {brief.skillMap
                    .filter((s) => !s.required)
                    .map((s) => (
                      <SkillRow key={s.name} skill={s} />
                    ))}
                </CollapsibleContent>
              </section>
            </Collapsible>

            {/* Career timeline */}
            <Collapsible
              open={openSections.timeline}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, timeline: open }))
              }
            >
              <section className="mt-4 rounded-xl border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2.5 px-5 py-4 text-left"
                    aria-expanded={openSections.timeline}
                  >
                    <Briefcase className="h-4 w-4 text-primary" />
                    <span className="text-sm font-bold text-foreground-strong">
                      Career timeline
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                      {brief.timeline.length} roles
                    </span>
                    <ChevronDown
                      className={cn(
                        'ml-auto h-4 w-4 text-muted-foreground transition-transform',
                        openSections.timeline && 'rotate-180'
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-5 pb-5">
                  <div className="relative ml-1.5 border-l-2 border-muted pl-5">
                    {brief.timeline.map((t) => (
                      <div
                        key={`${t.role}-${t.when}`}
                        className="relative pb-4 pt-1 last:pb-0"
                      >
                        <span
                          className={cn(
                            'absolute -left-[26px] top-2.5 h-2.5 w-2.5 rounded-full border-2 border-card',
                            t.current ? 'bg-primary' : 'bg-muted-foreground/40'
                          )}
                        />
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-[13px] font-semibold text-foreground-strong">
                            {t.role}
                          </span>
                          <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">
                            {t.when}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t.company}
                        </div>
                        {t.flags.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {t.flags.map((f) => (
                              <span
                                key={f}
                                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700"
                              >
                                <AlertTriangle className="h-2.5 w-2.5" />
                                {f}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </section>
            </Collapsible>

            {/* Question bank */}
            <QuestionnaireSection
              query={questionnaireQuery}
              status={questionnaire?.status}
              isEmpty={allQuestions.length === 0}
            >
              <>
                <section
                  className="mt-4 overflow-hidden rounded-xl border border-border bg-card"
                  aria-label="The two jobs of this interview"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-[13.5px] font-bold text-foreground-strong">
                        <UserCheck className="h-4 w-4 text-emerald-600" />
                        Verify before you credit it
                        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                          {verifyJobs.length}
                        </span>
                      </div>
                      <p className="mt-1 text-[11.5px] text-muted-foreground">
                        Claims the scorer matched. Each one has a question
                        waiting.
                      </p>
                      <div className="mt-2.5 flex flex-col">
                        {verifyJobs.map((item) => (
                          <JobItem
                            key={item.questionId}
                            item={item}
                            tone="v"
                            onJump={handleJump}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-border p-5 md:border-l md:border-t-0">
                      <div className="flex items-center gap-2 text-[13.5px] font-bold text-foreground-strong">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        Clarify before you decide
                        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                          {clarifyJobs.length}
                        </span>
                      </div>
                      <p className="mt-1 text-[11.5px] text-muted-foreground">
                        Discounted, missing, or unreadable to the parser. The
                        requirement needs these closed.
                      </p>
                      <div className="mt-2.5 flex flex-col">
                        {clarifyJobs.map((item) => (
                          <JobItem
                            key={item.questionId}
                            item={item}
                            tone="c"
                            onJump={handleJump}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                {QUESTION_GROUPS.map((group) => {
                  const qs = allQuestions.filter((q) => q.group === group.key);
                  if (!qs.length) return null;
                  const rated = qs.filter(
                    (q) => scorecard.ratings[q.id]
                  ).length;
                  const GroupIcon = group.icon;
                  return (
                    <Collapsible
                      key={group.key}
                      open={openSections[group.key]}
                      onOpenChange={(open) =>
                        setOpenSections((prev) => ({
                          ...prev,
                          [group.key]: open,
                        }))
                      }
                    >
                      <section className="mt-4 rounded-xl border border-border bg-card">
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2.5 px-5 py-4 text-left"
                            aria-expanded={openSections[group.key]}
                          >
                            <GroupIcon className="h-4 w-4 text-primary" />
                            <span className="text-sm font-bold text-foreground-strong">
                              {group.title}
                            </span>
                            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                              {rated} / {qs.length} rated
                            </span>
                            <ChevronDown
                              className={cn(
                                'ml-auto h-4 w-4 text-muted-foreground transition-transform',
                                openSections[group.key] && 'rotate-180'
                              )}
                            />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="px-5 pb-5">
                          <p className="-mt-1.5 mb-1 text-xs text-muted-foreground">
                            {group.sub}
                          </p>
                          {qs.map((q) => (
                            <QuestionCard
                              key={q.id}
                              question={q}
                              rating={scorecard.ratings[q.id]}
                              note={scorecard.notes[q.id] || ''}
                              pulsing={pulseId === q.id}
                              modelOpen={!!openModelAnswers[q.id]}
                              onToggleModel={() =>
                                setOpenModelAnswers((prev) => ({
                                  ...prev,
                                  [q.id]: !prev[q.id],
                                }))
                              }
                              guideOpen={!!openGuides[q.id]}
                              onToggleGuide={() =>
                                setOpenGuides((prev) => ({
                                  ...prev,
                                  [q.id]: !prev[q.id],
                                }))
                              }
                              onSave={(ratingValue, noteValue) =>
                                handleSaveQuestionResponse(
                                  q,
                                  ratingValue,
                                  noteValue
                                )
                              }
                              cardRef={(el) => {
                                questionRefs.current[q.id] = el;
                              }}
                              ratingLocked={!interviewStarted}
                              ratingLockedLabel={ratingLockedLabel}
                            />
                          ))}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  );
                })}
              </>
            </QuestionnaireSection>

            {/* Decision footer — hidden pre-interview alongside the per-question
            rating chips (see QuestionCard's ratingLocked prop): there's
            nothing to score or submit until the interview actually happens. */}
            {!interviewStarted ? (
              <div
                className="sticky bottom-3 z-20 mt-4 flex items-center gap-2 rounded-xl border border-dashed border-border bg-card p-4 text-[12.5px] text-muted-foreground"
                style={{ boxShadow: '0 -2px 10px rgba(16,32,51,0.08)' }}
                role="region"
                aria-label="Scorecard decision"
              >
                <Clock className="h-4 w-4 shrink-0" />
                {ratingLockedLabel}
              </div>
            ) : (
              <div
                className="sticky bottom-3 z-20 mt-4 rounded-xl border border-border bg-card p-4"
                style={{ boxShadow: '0 -2px 10px rgba(16,32,51,0.08)' }}
                role="region"
                aria-label="Scorecard decision"
              >
                <div className="flex flex-wrap items-center gap-3.5">
                  <span className="whitespace-nowrap font-mono text-[11.5px] font-semibold text-muted-foreground">
                    <b
                      className={cn(
                        'font-bold',
                        totalRated >= allQuestions.length
                          ? 'text-emerald-700'
                          : 'text-foreground-strong'
                      )}
                    >
                      {totalRated} / {allQuestions.length}
                    </b>{' '}
                    rated
                    {totalSkipped > 0 && (
                      <>
                        {' '}
                        ·{' '}
                        <b className="font-bold text-foreground-strong">
                          {totalSkipped}
                        </b>{' '}
                        skipped
                      </>
                    )}
                  </span>
                  {scorecard.submittedAt && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                      <Check className="h-3.5 w-3.5" />
                      Submitted · {scorecard.submittedAt} — final, can&apos;t be
                      changed
                    </span>
                  )}
                  {!scorecard.submittedAt && (
                    <div className="ml-auto">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* span wrapper — a disabled button swallows hover, so
                          the tooltip needs a non-disabled element to anchor
                          to. */}
                            <span>
                              <Button
                                size="sm"
                                className="gap-1.5"
                                disabled={!allRated}
                                onClick={() => setVerdictModalOpen(true)}
                              >
                                <Send className="h-3.5 w-3.5" />
                                Submit scorecard
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {!allRated && (
                            <TooltipContent>
                              Rate every question to submit — {totalRated} /{' '}
                              {allQuestions.length} so far.
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'jd' && (
          <JobDescriptionSection
            query={jdQuery}
            jobDescription={jobDescription}
            additionalRequirements={additionalRequirements}
          />
        )}
        <VerdictModal
          open={verdictModalOpen}
          onOpenChange={setVerdictModalOpen}
          candidateName={brief?.candidate.name}
          ratedCount={totalRated}
          totalCount={allQuestions.length}
          initialRecommendation={scorecard.recommendation}
          initialComments={scorecard.verdictComments}
          onSubmit={handleVerdictSubmit}
        />
        <VerdictSuccessDialog
          open={verdictSuccessOpen}
          onOpenChange={setVerdictSuccessOpen}
          candidateName={brief?.candidate.name}
          onDone={handleBackToUpcoming}
        />
        <CandidateProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          candidateId={brief?.candidate.candidateId}
        />
      </PageContainer>
      <NotesSticky
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        interviewRoundDetailId={idNum}
        onDirtyChange={setNotesDirty}
      />
    </div>
  );
}

export default InterviewBrief;
